import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, reload, sendEmailVerification, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import AuthForm from './AuthForm';
import DeleteAccountDialog from './DeleteAccountDialog';
import GuestFreeTap from './GuestFreeTap';
import { deleteTapTapAccount } from '../deleteAccount';
import { VERIFICATION_CONTINUE_URL } from '../verification';

export default function VerifiedAccess({ children }) {
  const [account, setAccount] = useState(null);
  const [ready, setReady] = useState(false);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [resendAfter, setResendAfter] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [guest, setGuest] = useState(false);
  useEffect(() => onAuthStateChanged(auth, user => {
    setAccount(user);
    setVerified(user?.emailVerified === true);
    setMessage('');
    setResendAfter(0);
    setDeleting(false);
    if (user) setGuest(false);
    setReady(true);
  }), []);

  useEffect(() => {
    if (!account || verified) return;
    let checking = false;
    const checkOnReturn = async () => {
      if (checking || document.visibilityState === 'hidden' || auth.currentUser?.uid !== account.uid) return;
      checking = true;
      try {
        await reload(account);
        if (auth.currentUser?.uid !== account.uid || !account.emailVerified) return;
        await account.getIdToken(true);
        if (auth.currentUser?.uid === account.uid) setVerified(true);
      } catch { /* Manual verification check remains available. */ }
      finally { checking = false; }
    };
    document.addEventListener('visibilitychange', checkOnReturn);
    window.addEventListener('focus', checkOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', checkOnReturn);
      window.removeEventListener('focus', checkOnReturn);
    };
  }, [account, verified]);

  const check = async () => {
    if (!account || busy) return;
    setBusy(true);
    try {
      await reload(account);
      if (auth.currentUser?.uid !== account.uid) return;
      if (!account.emailVerified) {
        setMessage('Your email is not verified yet. Open the link in your inbox, then try again.');
        return;
      }
      await account.getIdToken(true);
      if (auth.currentUser?.uid === account.uid) setVerified(true);
    } catch {
      setMessage('Could not check verification. Check your connection and try again.');
    } finally { setBusy(false); }
  };
  const resend = async () => {
    if (!account || busy || Date.now() < resendAfter) return;
    setBusy(true);
    try {
      await sendEmailVerification(account, { url: VERIFICATION_CONTINUE_URL, handleCodeInApp: false });
      setResendAfter(Date.now() + 60000);
      setMessage('Verification email sent. Check your inbox and spam folder. You can resend it after one minute.');
    } catch (error) {
      setMessage(error.code === 'auth/too-many-requests' ? 'Please wait a minute before requesting another email.' : 'Could not send the email. Please try again.');
    } finally { setBusy(false); }
  };
  if (!ready) return <div className="min-h-screen bg-black text-white grid place-items-center">Loading TapTap…</div>;
  if (!account) return guest ? <GuestFreeTap onSignIn={() => setGuest(false)} /> : <AuthForm onLogin={() => {}} onGuest={() => setGuest(true)} />;
  if (verified) return children;
  return <main className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
    <section className="w-full max-w-sm rounded-3xl border border-indigo-300/20 bg-slate-900 p-6 text-center">
      <h1 className="text-2xl font-bold">Verify your email</h1>
      <p className="mt-4 text-sm text-white/75">Open the verification link sent to <strong className="break-words">{account.email}</strong>, then return to TapTap. Check your spam folder too.</p>
      <button type="button" disabled={busy} onClick={check} className="mt-6 w-full min-h-11 rounded-xl bg-indigo-600 p-3 font-bold disabled:opacity-60">{busy ? 'Please wait…' : 'I’ve verified my email'}</button>
      <button type="button" disabled={busy} onClick={resend} className="mt-3 w-full min-h-11 rounded-xl bg-white/10 p-3 font-bold disabled:opacity-60">Resend verification email</button>
      {message && <p role="status" className="mt-4 text-sm text-indigo-100">{message}</p>}
      <button type="button" disabled={busy} onClick={() => signOut(auth).catch(() => setMessage('Could not sign out. Please try again.'))} className="mt-4 min-h-11 underline">Use a different account</button>
      <button type="button" disabled={busy} onClick={() => setDeleting(true)} className="mt-2 block w-full min-h-11 text-sm text-white/70 underline">Delete account</button>
    </section>
    {deleting && <DeleteAccountDialog onDelete={deleteTapTapAccount} onClose={() => setDeleting(false)} />}
  </main>;
}
